/**
 * EraToggle.tsx — Plan 04-04 Task 4 (ERA-01).
 *
 * DOM-level button that ONLY renders when the camera is inside a vistaZone
 * (`currentVistaId !== null`). Clicking the button flips `useSceneStore.era`
 * between '1925' and '2026'; EraGroup's useFrame picks up the change and
 * drives the 400ms opacity crossfade; EraPhotoOverlay's useTexture + damp
 * fades in the vista's 2026 photograph.
 *
 * Gating:
 *   - Hidden when `currentVistaId` is null — ERA-01 invariant (no scene-wide
 *     era toggle; only at 2-3 hero vistas)
 *   - Disabled during `eraTransitioning` — Pitfall #4 defense in depth
 *     (damp math would resolve correctly but users would lose confidence
 *     in the UI if the button fired mid-blend)
 *
 * Chrome: AESTHETIC-BRIEF Rule 7 — Goudy Old Style serif stack, cream
 * background (#FDF6EE), espresso text (#3B2F2F), 1px border. Mirrors
 * SourcesPanel styling so the paradata + era-toggle surfaces feel like
 * the same period-quiet chrome family.
 *
 * Position: fixed bottom-center at z-index 40. Leaves right-side SourcesPanel
 * (z-index 50, width 360px) undisturbed and sits above BoundaryFeedback (10).
 *
 * What this component does NOT do:
 *   - Auto-close when leaving a vista — VistaGate's useEffect handles the
 *     era=2026-on-vista-leave case by flipping era back to '1925' (WARNING 7
 *     closure); this component just hides itself via the gated return.
 *   - Snap camera yaw to photoRotation — deferred to Phase 6 when real
 *     ground-truth photos land (Open Question #1).
 */
import { useSceneStore } from '../store/useSceneStore';
import { useManifest } from '../content/useManifest';

const SERIF_STACK =
  '"Goudy Old Style", "Cormorant Garamond", Georgia, serif';
const CREAM = '#FDF6EE';
const ESPRESSO = '#3B2F2F';

export function EraToggle() {
  const currentVistaId = useSceneStore((s) => s.currentVistaId);
  const era = useSceneStore((s) => s.era);
  const eraTransitioning = useSceneStore((s) => s.eraTransitioning);
  const setEra = useSceneStore((s) => s.setEra);
  const { manifest } = useManifest();

  // ERA-01 gate — no vista, no toggle
  if (!currentVistaId) return null;

  const zone = manifest?.vistaZones.find((v) => v.id === currentVistaId);
  const targetEra = era === '1925' ? '2026' : '1925';
  const label = era === '1925' ? 'View 2026 Photograph' : 'Return to 1925';
  const context = zone?.label ?? '';

  const handleClick = () => {
    // Defense in depth — disabled prop also blocks this path, but we guard
    // again in case of programmatic dispatch (future keyboard shortcut etc.)
    if (eraTransitioning) return;
    setEra(targetEra);
  };

  return (
    <button
      type="button"
      aria-label={label}
      aria-disabled={eraTransitioning}
      disabled={eraTransitioning}
      onClick={handleClick}
      style={{
        position: 'fixed',
        bottom: 32,
        left: '50%',
        transform: 'translateX(-50%)',
        padding: '10px 24px',
        background: CREAM,
        color: ESPRESSO,
        border: `1px solid ${ESPRESSO}`,
        fontFamily: SERIF_STACK,
        fontSize: '0.95rem',
        letterSpacing: '0.05em',
        cursor: eraTransitioning ? 'default' : 'pointer',
        opacity: eraTransitioning ? 0.5 : 1,
        zIndex: 40,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        textAlign: 'center',
        minWidth: 220,
      }}
    >
      {label}
      {context && (
        <span
          style={{
            display: 'block',
            fontSize: '0.75rem',
            opacity: 0.7,
            marginTop: 2,
          }}
        >
          {context}
        </span>
      )}
    </button>
  );
}
